import React, { useState } from 'react';
import { ChangeType } from '../utils/diffEngine';

function TreeNode({ path, value, changeType, level = 0, onPathClick }) {
  const [isExpanded, setIsExpanded] = useState(level < 2);

  const isObject = value !== null && typeof value === 'object' && !Array.isArray(value);
  const isArray = Array.isArray(value);
  const hasChildren = isObject || isArray;

  const getClassName = () => {
    let className = 'tree-node';
    if (changeType === ChangeType.ADDED) className += ' node-added';
    if (changeType === ChangeType.REMOVED) className += ' node-removed';
    if (changeType === ChangeType.MODIFIED) className += ' node-modified';
    if (changeType === ChangeType.TYPE_CHANGED) className += ' node-type-changed';
    return className;
  };

  const formatPrimitiveValue = (val) => {
    if (val === null) return 'null';
    if (val === undefined) return 'undefined';
    if (typeof val === 'string') return `"${val}"`;
    return String(val);
  };

  const pathParts = path.split('.');
  const nodeName = pathParts[pathParts.length - 1];

  return (
    <div className={getClassName()} style={{ marginLeft: `${level * 20}px` }}>
      <div className="tree-node-header">
        {hasChildren && (
          <button
            className="tree-expand-btn"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        )}
        <button
          className="tree-node-name"
          onClick={() => onPathClick(path)}
          title={`Path: ${path}`}
        >
          {nodeName}
        </button>
        {!hasChildren && (
          <span className="tree-node-value">
            : {formatPrimitiveValue(value)}
          </span>
        )}
        {hasChildren && (
          <span className="tree-node-type">
            {isArray ? `[${value.length}]` : `{${Object.keys(value).length}}`}
          </span>
        )}
      </div>

      {hasChildren && isExpanded && (
        <div className="tree-node-children">
          {isArray ? (
            value.map((item, idx) => (
              <TreeNode
                key={idx}
                path={`${path}[${idx}]`}
                value={item}
                level={level + 1}
                onPathClick={onPathClick}
              />
            ))
          ) : (
            Object.entries(value).map(([key, val]) => (
              <TreeNode
                key={key}
                path={`${path}.${key}`}
                value={val}
                level={level + 1}
                onPathClick={onPathClick}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function TreeView({ original, modified, diffResult, onPathClick }) {
  const [activeView, setActiveView] = useState('both');

  if (!original && !modified) {
    return (
      <div className="tree-view empty">
        <div className="empty-state">
          <p className="empty-message">No data to display</p>
        </div>
      </div>
    );
  }

  // Build a change map from diffResult
  const changeMap = {};
  if (diffResult && diffResult.diffs) {
    diffResult.diffs.forEach(diff => {
      changeMap[diff.path] = diff.changeType;
    });
  }

  const renderTree = (data, label) => {
    if (!data) return null;

    return (
      <div className="tree-container">
        <h3 className="tree-header">{label}</h3>
        <div className="tree-content">
          {typeof data === 'object' ? (
            Object.entries(data).map(([key, value]) => (
              <TreeNode
                key={key}
                path={key}
                value={value}
                changeType={changeMap[key]}
                onPathClick={onPathClick}
              />
            ))
          ) : (
            <div className="tree-primitive">
              {JSON.stringify(data, null, 2)}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="tree-view">
      <div className="tree-view-controls">
        <button
          className={`view-btn ${activeView === 'original' ? 'active' : ''}`}
          onClick={() => setActiveView('original')}
        >
          Original
        </button>
        <button
          className={`view-btn ${activeView === 'both' ? 'active' : ''}`}
          onClick={() => setActiveView('both')}
        >
          Side-by-Side
        </button>
        <button
          className={`view-btn ${activeView === 'modified' ? 'active' : ''}`}
          onClick={() => setActiveView('modified')}
        >
          Modified
        </button>
      </div>

      <div className={`tree-view-content ${activeView}`}>
        {(activeView === 'original' || activeView === 'both') && renderTree(original, 'Original')}
        {(activeView === 'modified' || activeView === 'both') && renderTree(modified, 'Modified')}
      </div>
    </div>
  );
}
